'use strict'

import Utils from './Utils.js'
import ImageCanvas from './ImageCanvas.js'
import ColorSample from './models/ColorSample.js'
import CXImageAction from './ImagePaintProcessing/CXImageAction.js'
import CXImage from './ImagePaintProcessing/CXImage.js'
import Color from './models/Color.js'
import { DOM } from './ui/DOM.js'
import { Selector } from './ui/Selectors.js'
import PhotoAPI from './api/PhotoAPI.js'
import Dropcolor from './Dropcolor.js'
import ActionHistory from './ActionHistory.js'
import PolygonManager from './PolygonManager.js'
import { MaskColorSampleImporter } from './ui/MaskColorSamplePipeline.js'
import Config from './Config.js'

const MAX_IMAGE_SIZE = 1920 // 1536
const computeLuminosity = Utils.luminosity
const LIGHTNING_PARAM = [
    0.0, 0.001, 0.008, 0.027, 0.125, 0.2746, 0.512, 0.8574, 1.225, 1.728, 2.197, 2.8633, 3.5118, 4.4921, 5.3594, 6.3316, 7.4149, 8.6151,
    10.3602, 12.167, 14.1725, 16.5814, 19.0342, 21.952, 25.6724, 29.2181, 32.768, 35.937, 41.0636, 46.656, 51.4788, 57.9606, 64.9648,
    74.088, 85.184, 97.336, 114.0841, 132.651, 157.464, 185.193, 221.4451, 268.3361, 328.509, 413.4936, 541.3434, 729.0, 1030.301, 1728.0,
    3723.875, 125000.0, 125000.0,
]
const ActionType = Object.freeze({
    ActionTypeMagicPaint: 0,
    ActionTypePaint: 1,
    ActionTypeErase: 2,
    ActionTypeEraseMaskColor: 3,
    ActionTypeChangeColorSample: 4,
})
const RESERVED_UI_PX = 220

/**
 * CanvasState
 * Orchestrates canvas rendering, mask operations, and ColorSample sync.
 * Owns undo/redo history and coordinates UI refresh for painting workflows.
 */
class CanvasState {
    /**
     * @param {string} name
     * @param {Object} [opts]
     * @param {number} [opts.defaultTextureScale]
     * @param {Function|null} [opts.onDropcolorCreated]
     */
    constructor(name, { defaultTextureScale = Color.DEFAULT_TEXTURE_SCALE, onDropcolorCreated } = {}) {
        this.name = name

        this.defaultTextureScale = defaultTextureScale
        this._onDropcolorCreated = typeof onDropcolorCreated === 'function' ? onDropcolorCreated : null

        this.canvas = DOM.get(Selector.canvasDraw)

        this._canvasWidth = this.canvas.width
        this._canvasHeight = this.canvas.height

        this._realImageWidth = this.canvas.width
        this._realImageHeight = this.canvas.height
        this._canFit = false
        this._zoomed = false
        this._zoomScale = 1

        this._ratioCanvas = this._realImageHeight / this._canvasHeight

        this.ctx = this.canvas.getContext('2d')

        this.colorSample = new ColorSample()
        this._colorMeta = null
        this._colorMetaDirty = true
        // When ONE color changes (current slot)
        this.colorSample.onColorUIRender = (color) => {
            console.log('onColorUIRender')
            this._invalidateColorMeta()
            // visually update that tile
            Dropcolor.displayNewDropcolor(color)
            this._dispatchDropcolorEvent('viz:dropcolorColorUpdated', {
                slot: color?.dropcolor_id ?? this.colorSample?.workingColorPos ?? 0,
                color: color,
            })
        }

        // When the LIST changes (delete / move / undo / DB restore / prepaint, etc.)
        this.colorSample.onColorListChanged = (colorsMap) => {
            console.log('Color list changed, syncing dropcolor strip')
            this._invalidateColorMeta()

            const collection = document.getElementsByClassName('dropcolor')

            // 1. clear DOM + hide parents
            for (let i = 0; i < collection.length; i++) {
                const index = i + 1
                const $slot = $('#dropcolor' + index)

                // Remove any existing child tile (even if it has no text content)
                if (collection[i].children.length > 0) {
                    $slot.children('div:first').remove()
                    $slot.attr('style', '')
                    $('#deletedropcolor' + index).hide()
                }
                $slot.addClass('d-none')
            }

            // 2. render each color into its slot
            for (const [, color] of colorsMap) {
                Dropcolor.displayNewDropcolor(color)
            }

            // 3. show only used dropcolor + 1
            for (let i = 0; i < colorsMap.size + 1; i++) {
                $('#dropcolor' + (i + 1)).removeClass('d-none')
            }

            // 4. recompute full/empty state
            Dropcolor.rebuildSlots()

            // 5. re-apply selection
            Dropcolor.selectSlot(this.colorSample?.workingColorPos ?? 0)

            this._dispatchDropcolorEvent('viz:dropcolorListChanged', {
                slot: this.colorSample?.workingColorPos ?? 0,
                color: this.colorSample?.getCurrentColor?.() || null,
            })
        }

        // Polygon manager holds in-progress polygon state
        this.polygonManager = new PolygonManager({ canvasState: this })

        this.isPainting = false

        // Luminosity LUT (read-only)
        this.lightningParam = LIGHTNING_PARAM

        // Canvas
        this.viewimage = new ImageCanvas('viewimage')
        this.viewimage_gray = new ImageCanvas('viewimage_gray')
        this.viewimage_draw = new ImageCanvas('viewimage_draw')
        this.viewimage_original = new ImageCanvas('viewimage_original')
        this.viewimageDiv = DOM.get(Selector.canvasDiv)

        this._setupLayoutObservation()

        this.viewimage_texture_5 = new ImageCanvas('viewimage_texture_5')
        this.viewimage_texture_1 = new ImageCanvas('viewimage_texture_1')
        this.viewimage_texture_2 = new ImageCanvas('viewimage_texture_2')
        this.viewimage_texture_3 = new ImageCanvas('viewimage_texture_3')
        this.viewimage_texture_4 = new ImageCanvas('viewimage_texture_4')

        this._maskImage = new CXImage()

        // Action history (undo/redo stack)
        this.actionHistory = new ActionHistory({ canvasState: this, actionTypes: ActionType })

        // Keep brush edges crisp on the draw layer.
        this.setDrawSmoothing(false)
    }

    /**
     * Reset this CanvasState so it can be reused for a new project/image
     * without creating a new instance.
     */
    resetForNewProject() {
        console.log('CanvasState.resetForNewProject')

        // Stop any drawing state
        this.isPainting = false
        this.polygonManager.reset()
        // Clear all visible canvases
        this.clearCanvas()
        const clearCanvas = (canvas) => {
            if (!canvas?.context) return
            canvas.context.clearRect(0, 0, canvas.width, canvas.height)
        }
        ;[this.viewimage_gray, this.viewimage_draw, this.viewimage_original].forEach(clearCanvas)

        // Clear all texture canvases too
        const textures = [
            this.viewimage_texture_1,
            this.viewimage_texture_2,
            this.viewimage_texture_3,
            this.viewimage_texture_4,
            this.viewimage_texture_5,
        ]
        textures.forEach((tex) => {
            if (tex?.context) {
                tex.context.clearRect(0, 0, tex.width, tex.height)
            }
        })

        // Reset mask image to a fresh empty buffer
        if (this._realImageWidth && this._realImageHeight) {
            this._maskImage.initWithSize(this._realImageWidth, this._realImageHeight)
        }

        // Reset undo/redo stack
        this.actionHistory.reset()

        // Reset color sample (but keep callbacks & instance)
        this.colorSample.colors = new Map()
        this.colorSample.workingColorPos = ColorSample.DEFAULT_WORKING_COLOR_POS
        this.colorSample.updateColorInfos()
        this._invalidateColorMeta()

        // Reload internal ImageData caches from now-empty canvases
        this.viewimage.loadData()
        this.viewimage_original.loadData()
        this.viewimage_gray.loadData()
    }

    /**
     * Dispatch a DOM event for dropcolor updates.
     * @param {string} name
     * @param {Object} detail
     * @returns {void}
     */
    _dispatchDropcolorEvent(name, detail) {
        if (typeof document === 'undefined') return
        document.dispatchEvent(
            new CustomEvent(name, {
                detail,
            }),
        )
    }

    _invalidateColorMeta() {
        this._colorMetaDirty = true
    }

    _buildColorMeta(colorCount) {
        const bleachA = new Array(colorCount)
        const bleachB = new Array(colorCount)
        const swatchRGB = new Array(colorCount)
        const textureData = new Array(colorCount)
        const lightningParam = this.lightningParam

        for (let i = 1; i <= colorCount; i++) {
            const color = this.colorSample.getColorByNumber(i)
            const index = i - 1
            const bleach = lightningParam[color.bleach_value]

            bleachA[index] = bleach
            bleachB[index] = bleach + 255

            const swatchEntry = color.swatch_color || null
            const hasTextureData = !!(color.texture_data && color.texture_data.data && color.texture_data.data.length)

            if (color.type === Color.TYPE.COLOR) {
                swatchRGB[index] = swatchEntry
                textureData[index] = null
            } else if (hasTextureData) {
                textureData[index] = color.texture_data.data
                swatchRGB[index] = null
            } else {
                swatchRGB[index] = swatchEntry
                textureData[index] = null
            }
        }

        return {
            colorCount,
            bleachA,
            bleachB,
            swatchRGB,
            textureData,
        }
    }

    _getColorMeta() {
        const colorCount = this.colorSample.numberOfColors()
        if (this._colorMetaDirty || !this._colorMeta || this._colorMeta.colorCount !== colorCount) {
            this._colorMeta = this._buildColorMeta(colorCount)
            this._colorMetaDirty = false
        }
        return this._colorMeta
    }

    /**
     * Notify external listeners when a dropcolor is created.
     * @param {Color} color
     * @returns {void}
     */
    _notifyDropcolorCreated(color) {
        console.log('_notifyDropcolorCreated')
        if (this._onDropcolorCreated) {
            this._onDropcolorCreated(color)
        }
    }

    /**
     * Simple: fit once when the page is loaded, and on window resizes.
     * No timeouts, no polling. We also listen to a stable container with ResizeObserver
     * if available, but this is optional.
     */
    _setupLayoutObservation() {
        const flexRow = this.viewimageDiv?.closest?.('.flex-core') || this.viewimageDiv?.parentElement || this.viewimageDiv

        const safeFit = () => {
            if (!this._canFit) return // wait until setImageSize() ran
            if (flexRow && flexRow.clientWidth === 0) return // ignore hidden/collapsed
            this.fitToViewport()
        }

        // Fit once when the whole page is ready (fonts, CSS, etc.)
        if (document.readyState === 'complete') {
            safeFit()
        } else {
            window.addEventListener('load', safeFit, { once: true })
        }

        // Fit on browser resize
        window.addEventListener('resize', safeFit, { passive: true })

        // Optional: also react when the actual layout container changes size
        if (window.ResizeObserver && flexRow) {
            try {
                if (this._ro && this._ro.disconnect) this._ro.disconnect()
                this._ro = new ResizeObserver(() => safeFit())
                this._ro.observe(flexRow)
            } catch {
                // ignore; window resize is enough
            }
        }
    }

    async loadProjectFromDatabase(data, toolManager, current_swatch_id, listOfColorChartsServerID) {
        console.log('loadProjectFromDatabase')

        // Visualizer edit route opens one active photo; use the first hydrated photo record.
        const firstPhoto = data.photos[0]

        // load image
        const originalURL = PhotoAPI.imageURL(firstPhoto.id)
        const img = await Utils.loadImage(originalURL)
        this._drawImageToCanvases(img)
        toolManager.initImageSegmentationProcessor()

        // mask texture
        const maskURL = PhotoAPI.maskURL(firstPhoto.id)

        try {
            const maskTextureImageData = await this._loadMaskTextureImageDataFromUrl(maskURL)
            const colorsByColorSamplePosition = Utils.mapColorsByColorSamplePosition(firstPhoto.color_sample.colors)
            await this.applyMaskColorSampleAndTexture({
                colorArray: colorsByColorSamplePosition,
                maskTextureImageData: maskTextureImageData,
                current_swatch_id: current_swatch_id,
                listOfColorChartsServerID: listOfColorChartsServerID,
            })
        } catch (e) {
            console.error('Mask application failed', e)
        }
    }

    static get maxImageSize() {
        return MAX_IMAGE_SIZE
    }

    /**
     * Clear canvas before redraw
     * @private
     * @method clearCanvas
     * @return {Object} description
     */
    clearCanvas() {
        console.log('--- clearCanvas ---')
        this.viewimage.context.clearRect(0, 0, this.viewimage.width, this.viewimage.height)
    }

    /**
     * Push an action to the undo stack and update button states.
     * @param {CXImageAction} action
     * @returns {void}
     */
    addAction(action) {
        this.actionHistory.add(action)
    }

    get actions() {
        return this.actionHistory.actions
    }

    /**
     * Return the most recent action from the stack.
     * @returns {CXImageAction|undefined}
     */
    getLastAction() {
        return this.actionHistory.getLastAction()
    }

    /**
     * Return the most recent magic-paint action.
     * @returns {CXImageAction|null}
     */
    getLastMagicPaintAction() {
        return this.actionHistory.getLastMagicPaintAction()
    }

    /**
     * Restore ColorSample colors + workingColorPos from a snapshot.
     * Ensures:
     *   - Map keys are numeric
     *   - color.dropcolor_id matches the key
     *   - DOM is rebuilt via updateColorInfos()
     *
     * @param {Iterable<[number|string, Object]>} snapshotColors
     * @param {number} snapshotWorkingPos
     */
    _restoreColorSampleFromSnapshot(snapshotColors, snapshotWorkingPos) {
        const rebuilt = new Map()

        for (const [key, color] of snapshotColors || []) {
            const n = parseInt(key, 10)
            if (!Number.isFinite(n) || n <= 0) continue

            // keep dropcolor_id in sync with the map index
            if (color && typeof color === 'object') {
                color.dropcolor_id = n
            }
            rebuilt.set(n, color)
        }

        this.colorSample.colors = rebuilt
        this.colorSample.workingColorPos = snapshotWorkingPos || 0

        // This will:
        //   - clamp workingColorPos
        //   - call onColorListChanged(colors) → rebuild .dropcolor DOM
        this.colorSample.updateColorInfos()
    }

    /**
     * Undo the last action and refresh the mask/canvas state.
     * @returns {void}
     */
    undo() {
        this.actionHistory.undo()
    }

    /**
     * Redo the last undone action.
     * @returns {void}
     */
    redo() {
        this.actionHistory.redo()
    }

    /**
     * Apply smoothing settings to a 2D canvas context.
     * @param {CanvasRenderingContext2D} ctx
     * @param {boolean} enabled
     * @returns {void}
     */
    _setContextSmoothing(ctx, enabled) {
        if (!ctx) return
        ctx.imageSmoothingEnabled = enabled
        ctx.oImageSmoothingEnabled = enabled
        ctx.webkitImageSmoothingEnabled = enabled
        ctx.msImageSmoothingEnabled = enabled
    }

    /**
     * Enable/disable smoothing on the main drawing context.
     * @param {boolean} enabled
     * @returns {void}
     */
    setDrawSmoothing(enabled) {
        this._setContextSmoothing(this.ctx, enabled)
    }

    get realImageWidth() {
        return this._realImageWidth
    }

    get realImageHeight() {
        return this._realImageHeight
    }

    /**
     * Get ImageData from original image
     * @param {*} x1 x1 value
     * @param {*} y1 y1 value
     * @param {*} x2 x2 value
     * @param {*} y2 y2 value
     * @returns ImageData object
     */
    getOriginalImageImageData(x1 = 0, y1 = 0, x2 = this._realImageWidth, y2 = this._realImageHeight) {
        return this.viewimage_original.context.getImageData(x1, y1, x2, y2)
    }

    /**
     * Get ImageData from result image (painted image)
     * @param {*} x1 x1 value
     * @param {*} y1 y1 value
     * @param {*} x2 x2 value
     * @param {*} y2 y2 value
     * @returns ImageData object
     */
    getResultImageImageData(x1 = 0, y1 = 0, x2 = this._realImageWidth, y2 = this._realImageHeight) {
        return this.viewimage.context.getImageData(x1, y1, x2, y2)
    }

    /**
     * Get ImageData from black and white image
     * @param {*} x1 x1 value
     * @param {*} y1 y1 value
     * @param {*} x2 x2 value
     * @param {*} y2 y2 value
     * @returns ImageData object
     */
    getGrayImageImageData(x1 = 0, y1 = 0, x2 = this._realImageWidth, y2 = this._realImageHeight) {
        return this.viewimage_gray.context.getImageData(x1, y1, x2, y2)
    }

    /**
     * Set real image size
     * @param {*} w width
     * @param {*} h height
     */
    setImageSize(w, h) {
        console.log('setImageSize (' + w + ',' + h + ') :ratio: ' + w / h)

        this._realImageWidth = w
        this._realImageHeight = h

        // Need to reduce image size
        const maxSide = Math.max(w, h)
        if (maxSide > CanvasState.maxImageSize) {
            const k = CanvasState.maxImageSize / maxSide
            this._realImageWidth = Math.round(w * k)
            this._realImageHeight = Math.round(h * k)
            console.log('new dimensions:', this._realImageWidth, this._realImageHeight)
        }

        this.viewimage.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_gray.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_draw.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_original.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_texture_5.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_texture_1.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_texture_2.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_texture_3.size(this._realImageWidth, this._realImageHeight)
        this.viewimage_texture_4.size(this._realImageWidth, this._realImageHeight)

        this._canFit = true
        this.fitToViewport()
    }

    /**
     * Collect all canvas layers that should scale together in the viewport.
     * @returns {ImageCanvas[]} Array of ImageCanvas wrappers.
     * @private
     */
    _getDisplayCanvases() {
        return [
            this.viewimage,
            this.viewimage_gray,
            this.viewimage_draw,
            this.viewimage_original,
            this.viewimage_texture_1,
            this.viewimage_texture_2,
            this.viewimage_texture_3,
            this.viewimage_texture_4,
            this.viewimage_texture_5,
        ]
    }

    /**
     * Apply a visual scale to all canvas layers and update display metrics.
     * @param {number} scale - Scale multiplier (1.0 = 100%).
     * @param {Object} [options]
     * @param {boolean} [options.zoomed=false] - Whether zoom mode is active.
     * @private
     */
    _applyDisplayScale(scale, { zoomed = false } = {}) {
        if (!isFinite(scale) || scale <= 0) return

        const dispW = Math.round(this._realImageWidth * scale)
        const dispH = Math.round(this._realImageHeight * scale)

        const allowOverflow = zoomed

        this._getDisplayCanvases().forEach((c) => {
            if (!c?.element) return
            c.element.style.setProperty('width', dispW + 'px', 'important')
            c.element.style.setProperty('height', dispH + 'px', 'important')

            if (allowOverflow) {
                c.element.style.setProperty('max-width', 'none', 'important')
                c.element.style.setProperty('max-height', 'none', 'important')
            } else {
                c.element.style.removeProperty('max-width')
                c.element.style.removeProperty('max-height')
            }
        })

        this._canvasWidth = dispW
        this._canvasHeight = dispH
        this._ratioCanvas = this._realImageHeight / this._canvasHeight

        if (this.viewimageDiv) {
            this.viewimageDiv.classList.toggle('is-zoomed', zoomed)
        }
    }

    /**
     * Enable zoom mode and apply a given zoom scale.
     * @param {number} scale - Scale multiplier (clamped to 1.0–2.0).
     */
    setZoomScale(scale) {
        const next = Math.min(2, Math.max(1, Number(scale) || 1))
        const view = this.viewimageDiv
        const prevScale = this._zoomed
            ? this._zoomScale
            : this._fitScale || (this._canvasWidth && this._realImageWidth ? this._canvasWidth / this._realImageWidth : 1)
        const canAnchor = view && view.clientWidth > 0 && view.clientHeight > 0 && prevScale > 0
        const anchor = canAnchor
            ? {
                  x: (view.scrollLeft + view.clientWidth / 2) / prevScale,
                  y: (view.scrollTop + view.clientHeight / 2) / prevScale,
              }
            : null

        this._zoomScale = next
        this._zoomed = true
        this.fitToViewport()

        if (anchor && view) {
            const targetLeft = anchor.x * next - view.clientWidth / 2
            const targetTop = anchor.y * next - view.clientHeight / 2
            view.scrollLeft = Math.max(0, targetLeft)
            view.scrollTop = Math.max(0, targetTop)
        }
    }

    /**
     * Disable zoom mode and return to fit-to-viewport scaling.
     */
    disableZoom() {
        this._zoomed = false
        this.fitToViewport()
    }

    /**
     * Read whether zoom mode is active.
     * @returns {boolean}
     */
    isZoomed() {
        return this._zoomed
    }

    /**
     * Read the current zoom scale.
     * @returns {number}
     */
    getZoomScale() {
        return this._zoomScale
    }

    /**
     * Compute the best-fit scale and container metrics for the canvas display.
     * @returns {{ fitScale: number, canFit: boolean }}
     * @private
     */
    _getViewportFitMetrics() {
        // Vertical constraint (leave room for top UI)
        const vh = window.innerHeight || document.documentElement.clientHeight || this._realImageHeight
        let maxDisplayH = Math.max(200, vh - RESERVED_UI_PX)

        // Anchors
        const previewEl = document.getElementById('preview') // has .p-2
        const flexCore = this.viewimageDiv?.closest('.flex-core') || this.viewimageDiv?.parentElement || document.documentElement
        const leftCol = document.getElementById('leftcolumn')
        const centerCol = document.getElementById('centercolumn')
        let containerW = 0
        let containerH = 0

        if (previewEl && flexCore && centerCol) {
            // Legacy layout: derive the remaining width for the center column.
            const csPreview = window.getComputedStyle(previewEl)
            const padPreviewX = (parseFloat(csPreview.paddingLeft) || 0) + (parseFloat(csPreview.paddingRight) || 0)
            const previewW = previewEl.getBoundingClientRect().width || 0
            const previewInner = Math.max(0, previewW - padPreviewX)

            // left column width and its right padding
            const leftW = leftCol?.getBoundingClientRect?.().width || 0
            const leftPadR = leftCol ? parseFloat(window.getComputedStyle(leftCol).paddingRight) || 0 : 0

            // horizontal gap between the two flex items
            const csFlex = window.getComputedStyle(flexCore)
            const gap = parseFloat(csFlex.columnGap || csFlex.gap || 0) || 0

            // remaining width for center column after subtracting preview padding, left column, gap, and left right-padding
            const remaining = Math.max(0, previewInner - leftW - gap - leftPadR - 10)

            // lock center column to that width so it truly occupies the rest
            centerCol.style.flexBasis = remaining + 'px'
            centerCol.style.maxWidth = remaining + 'px'
            centerCol.style.width = remaining + 'px'

            containerW = remaining
        } else {
            // Use the current canvas container when layout anchors are missing.
            const fallbackContainer = document.getElementById('viz-canvas') || this.viewimageDiv?.parentElement || this.viewimageDiv
            if (fallbackContainer) {
                const rect = fallbackContainer.getBoundingClientRect()
                const csContainer = window.getComputedStyle(fallbackContainer)
                const padX = (parseFloat(csContainer.paddingLeft) || 0) + (parseFloat(csContainer.paddingRight) || 0)
                const padY = (parseFloat(csContainer.paddingTop) || 0) + (parseFloat(csContainer.paddingBottom) || 0)
                containerW = Math.max(0, rect.width - padX)
                containerH = Math.max(0, rect.height - padY)
            }
        }

        if (containerH) {
            maxDisplayH = Math.min(maxDisplayH, containerH)
        }

        const canFit = containerW && containerW >= 50
        const fitScale = canFit ? Math.min(1, maxDisplayH / this._realImageHeight, containerW / this._realImageWidth) : 1

        return { fitScale, canFit }
    }

    /**
     * Fit the canvas display to the available viewport area.
     * @returns {void}
     */
    fitToViewport() {
        if (!this._canFit) return
        console.log('fitToViewport')

        const zoomed = !!this._zoomed
        const zoomScale = Number(this._zoomScale) || 1
        const { fitScale, canFit } = this._getViewportFitMetrics()
        this._fitScale = fitScale

        if (zoomed) {
            this._applyDisplayScale(zoomScale, { zoomed: true })
            return
        }

        if (!canFit) return

        this._applyDisplayScale(fitScale, { zoomed: false })
    }

    /**
     * Convert image data to grayscale and update the gray canvas.
     * @param {ImageData} imageDataSource
     * @returns {void}
     */
    drawImageInGrayscale(imageDataSource) {
        console.log('drawImageInGrayscale')

        for (let i = 0; i < imageDataSource.data.length; i += 4) {
            // Weighted gray conversion tuned to preserve perceived luminance.
            const brightness = 0.34 * imageDataSource.data[i] + 0.5 * imageDataSource.data[i + 1] + 0.16 * imageDataSource.data[i + 2]
            imageDataSource.data[i] = brightness
            imageDataSource.data[i + 1] = brightness
            imageDataSource.data[i + 2] = brightness
        }

        this.viewimage_gray.context.putImageData(imageDataSource, 0, 0)
        this.viewimage_gray.loadData()
    }

    /**
     * Populate mask data from a source image and refresh the display.
     * @param {ImageData} imageData
     * @param {number} shiftCounter
     * @returns {void}
     */
    async registerAndDisplayMasks(imageData, shiftCounter) {
        console.log('registerAndDisplayMasks: ' + this.colorSample.numberOfColors())

        const start = Date.now()

        // Set the unpainted Pixels counters and the first action
        const firstPaint = new CXImageAction(ActionType.ActionTypePaint)

        // Set maskImage
        this._maskImage.initWithImageData(imageData, shiftCounter)

        // Set firstPaint
        const maxIter = this._maskImage.data.length
        for (let i = 0; i < maxIter; i++) {
            const v = this._maskImage.data[i]
            if (v > 0 && v !== Config.DEFAULT_MASKING_COLOR && v <= this.colorSample.numberOfColors()) {
                firstPaint.lastLevelpixels().addPixelwithValue(i, v)
            }
        }

        if (firstPaint.lastLevelpixels().count() > 0) {
            firstPaint.color = this.colorSample
            this.addAction(firstPaint)
        }
        const end = Date.now()
        console.log(`Execution time: ${end - start} ms`)
        this.actionHistory.clearRedo()
    }

    /**
     * Return the current polygon being drawn.
     * @returns {Polygon|undefined}
     */
    getCurrentPolygon() {
        return this.polygonManager.getCurrentPolygon()
    }

    /**
     * Start a new polygon and register it.
     * @param {Object} opts
     * @param {number} opts.x
     * @param {number} opts.y
     * @param {Object} opts.color
     * @param {number|string} opts.numcolor
     * @returns {Polygon}
     */
    startPolygon({ x, y, color, numcolor }) {
        return this.polygonManager.startPolygon({ x, y, color, numcolor })
    }

    /**
     * Register an existing polygon instance.
     * @param {Polygon} polygon
     * @returns {void}
     */
    registerPolygon(polygon) {
        this.polygonManager.registerPolygon(polygon)
    }

    /**
     * Convert pixel coordinates into a mutable pixel list.
     * @param {Map<number, {x:number,y:number}>} points
     * @param {number} position
     * @returns {CXGrayImagePixelMutableArray}
     */
    buildPixelArrayFromPoints(points, position) {
        return this.polygonManager.buildPixelArrayFromPoints(points, position)
    }

    /**
     * Finish the polygon and apply its pixels.
     * @param {boolean} fill
     * @returns {void}
     */
    finishPolygon(fill) {
        this.polygonManager.finishPolygon(fill)
    }

    /**
     * Discard the last polygon in the stack.
     * @returns {Polygon|undefined}
     */
    discardLastPolygon() {
        return this.polygonManager.discardLastPolygon()
    }

    /**
     * Recompute canvas dimensions based on the current DOM size.
     * @returns {void}
     */
    setNewCanvasDimension() {
        console.warn('setNewCanvasDimension')
        const rect = this.viewimage?.element?.getBoundingClientRect?.()
        if (rect?.width && rect?.height) {
            this._canvasHeight = rect.height
            this._canvasWidth = rect.width
        } else if (this.viewimageDiv) {
            this._canvasHeight = this.viewimageDiv.clientHeight
            this._canvasWidth = this.viewimageDiv.clientWidth
        }

        if (this._canvasHeight) {
            this._ratioCanvas = this._realImageHeight / this._canvasHeight
        }
    }

    // ----------------------------------------------------------------------------------------------------------------------
    // PAINTING METHODS
    // ----------------------------------------------------------------------------------------------------------------------

    /**
     * Recompute the full painted image from the current mask + color sample.
     * @returns {void}
     */
    rebuildPaintedImage() {
        // refresh if colorsample has colors
        if (this.colorSample.numberOfColors() === 0) {
            console.warn('no color')
            return
        }

        const imageMaskLength = this._maskImage.length
        const grayData = this.viewimage_gray.data?.data
        const results = this.getOriginalImageImageData()
        const { bleachA, bleachB, swatchRGB, textureData } = this._getColorMeta()

        for (let i = 0; i < imageMaskLength; i += 1) {
            const maskColor = this._maskImage.data[i]
            if (maskColor <= 0 || maskColor === Config.DEFAULT_MASKING_COLOR) continue

            const arrayIndex = maskColor - 1
            const pos = i * 4

            let colorResult = null
            if (textureData[arrayIndex]) {
                const texture = textureData[arrayIndex]
                colorResult = computeLuminosity(
                    bleachA[arrayIndex],
                    bleachB[arrayIndex],
                    grayData[pos],
                    texture[pos],
                    texture[pos + 1],
                    texture[pos + 2],
                )
            } else if (swatchRGB[arrayIndex]) {
                const rgb = swatchRGB[arrayIndex]
                colorResult = computeLuminosity(
                    bleachA[arrayIndex],
                    bleachB[arrayIndex],
                    grayData[pos],
                    rgb.sr,
                    rgb.sg,
                    rgb.sb,
                )
            } else {
                const original = this.viewimage_original.data.data
                results.data[pos] = original[pos]
                results.data[pos + 1] = original[pos + 1]
                results.data[pos + 2] = original[pos + 2]
                continue
            }

            results.data[pos] = colorResult[1]
            results.data[pos + 1] = colorResult[2]
            results.data[pos + 2] = colorResult[3]
        }

        this.viewimage.context.putImageData(results, 0, 0)
        this.viewimage.loadData()
    }

    /**
     * Refresh only pixels affected by a specific ColorSample index.
     * @param {number} colorsample_index
     * @returns {void}
     */
    refreshSwatchColor(colorsample_index) {
        this._invalidateColorMeta()
        const meta = this._getColorMeta()
        this.viewimage.loadData()
        const imageMaskLength = this._maskImage.length
        for (let i = 0; i < imageMaskLength; i += 1) {
            if (this._maskImage.data[i] === colorsample_index) {
                this.refreshResultAtPosition([i, this._maskImage.data[i]], meta)
            }
        }
        this.viewimage.context.putImageData(this.viewimage.data, 0, 0)
    }

    /**
     *
     * @param {Uint32Array([pos, mask_value]} pixel
     */
    refreshResultAtPosition(pixel, meta = null) {
        const maskColor = this._maskImage.data[pixel[0]]

        let newRGBColor = []
        const pixelOffset = pixel[0] * 4
        if (maskColor === Config.DEFAULT_MASKING_COLOR) {
            newRGBColor[1] = this.viewimage_original.data.data[pixelOffset]
            newRGBColor[2] = this.viewimage_original.data.data[pixelOffset + 1]
            newRGBColor[3] = this.viewimage_original.data.data[pixelOffset + 2]
        } else {
            const newGrayScale = this.viewimage_gray.data.data[pixelOffset]
            const { bleachA, bleachB, swatchRGB, textureData } = meta || this._getColorMeta()
            const arrayIndex = pixel[1] - 1
            const bleachAVal = bleachA[arrayIndex]
            const bleachBVal = bleachB[arrayIndex]
            const texData = textureData[arrayIndex]

            if (texData) {
                // If the color is a texture then we get the pixel correponding to the pixel we are painting
                // Due to the fact that the image is bigger then the texture we need to use the modulos to get the pixel
                newRGBColor = computeLuminosity(
                    bleachAVal,
                    bleachBVal,
                    newGrayScale,
                    texData[pixelOffset],
                    texData[pixelOffset + 1],
                    texData[pixelOffset + 2],
                )
            } else if (swatchRGB[arrayIndex]) {
                const rgb = swatchRGB[arrayIndex]
                newRGBColor = computeLuminosity(
                    bleachAVal,
                    bleachBVal,
                    newGrayScale,
                    rgb.sr,
                    rgb.sg,
                    rgb.sb,
                )
            } else {
                newRGBColor[1] = this.viewimage_original.data.data[pixelOffset]
                newRGBColor[2] = this.viewimage_original.data.data[pixelOffset + 1]
                newRGBColor[3] = this.viewimage_original.data.data[pixelOffset + 2]
            }
        }

        this.viewimage.data.data[pixelOffset] = newRGBColor[1]
        this.viewimage.data.data[pixelOffset + 1] = newRGBColor[2]
        this.viewimage.data.data[pixelOffset + 2] = newRGBColor[3]
    }

    /**
     * Apply a new swatch color to the current slot and preserve relevant metadata.
     * @param {Color} new_swatch_color
     * @param {Color|undefined} old_color
     * @param {string|undefined} backgroundImageURL
     * @param {number|string|undefined} textureID
     * @param {SwatchSelectionState} swatchSelection
     * @returns {Promise<void>}
     */
    async setCurrentColorFromChart(new_swatch_color, old_color, backgroundImageURL, textureID, swatchSelection) {
        console.info('setCurrentColorFromChart')

        let color = this.colorSample.getColorByNumber(this.colorSample.workingColorPos)
        color = new_swatch_color
        color.dropcolor_id = this.colorSample.workingColorPos

        if (typeof old_color !== 'undefined') {
            console.info('old_color exist')

            // Copy old properties if old color is texture / texture swatch
            if (old_color.type === Color.TYPE.TEXTURE || old_color.type === Color.TYPE.TEXTURE_SWATCH) {
                console.info('old color has texture / texture swatch')
                color.texture_scale = Utils.computeTextureScale(
                    old_color.texture_scale,
                    old_color.texturePixelResolution,
                    color.texturePixelResolution,
                )
                color.texture_rotation = old_color.texture_rotation
            }

            color.bleach_value = old_color.bleach_value

            // Preserve user-entered metadata
            color.name = old_color.name
            color.note = old_color.note
        }

        const isTexture = new_swatch_color.type === Color.TYPE.TEXTURE || new_swatch_color.type === Color.TYPE.TEXTURE_SWATCH
        if (isTexture) {
            // load image
            const img = await Utils.loadImage(backgroundImageURL)
            console.info('new color has texture / texture swatch')
            color.texture_canvas_name = 'viewimage_texture_' + this.colorSample.workingColorPos
            color.texture_data_base64 = Utils.getBase64Image(img)
            color.texture_full_url = backgroundImageURL
            color.texture_scale = this.defaultTextureScale // Default value
            color.texture_rotation = Color.DEFAULT_TEXTURE_ROTATION // Default value
            color.texturePixelResolution = img.naturalWidth // Default value
            // todo textureSizeInMM

            if (new_swatch_color.type === Color.TYPE.TEXTURE_SWATCH) {
                // Get texture from DB
                const texture = Utils.getTextureFromID(textureID, swatchSelection)

                // add texture to swatch color
                color.texture = texture.texture
                color.texture_page = texture.texture_page
                color.texture_swatch = texture.texture_swatch
            }

            this.colorSample.setCurrentColor(color)
            return
        }

        console.info('new color is a color, delete')
        // todo remove texture properties
        delete color.texture_canvas_name
        delete color.texture_data_base64
        delete color.texture_full_url
        delete color.texture_scale
        delete color.texture_rotation
        delete color.texturePixelResolution
        delete color.textureSizeInMM

        // save
        this.colorSample.setCurrentColor(color)
    }

    /**
     * Load the texture image (base64) and redraw the destination canvas.
     * @param {number} [position=this.colorSample.workingColorPos] - swatch/slot index
     */
    refreshTextureCanvas(position = this.colorSample.workingColorPos) {
        console.log('refreshTextureCanvas: ' + position)

        // Lookup color/texture data
        const colorObject = this.colorSample.getColorByNumber(position) // swatch_color from colorSample

        // Prepare image
        const img = new Image() // texture from dataUrl
        img.decoding = 'async' // best-effort hint (does not change logic)
        const self = this // preserve 'this' inside onload

        // When the texture is ready, render and refresh results
        img.onload = function () {
            self.renderTextureCanvas(colorObject, position, img)
            self._invalidateColorMeta()
            self.rebuildPaintedImage()
        }

        // Basic error logging (non-intrusive)
        img.onerror = (e) => console.error('Texture image failed to load', e)

        // Start loading the base64 texture
        img.src = colorObject.texture_data_base64
    }

    /**
     * Draw the repeating, rotated texture pattern into the per-position canvas.
     * Logic/order kept exactly as in your original implementation.
     *
     * @param {Object} colorObject - contains rotation/scale/tint info
     * @param {number} position - swatch/slot index used to resolve the canvas element
     * @param {HTMLImageElement} img - loaded texture image (source for the tile)
     */
    renderTextureCanvas(colorObject, position, img) {
        // --- setup & inputs ------------------------------------------------------
        const tempCanvas = document.createElement('canvas') // holds one square texture tile
        const angleInRad = Utils.degreeToRad(colorObject.texture_rotation) // texture rotation (radians)

        // Compute target tile size (square) — same math, clamped & rounded
        const textureNewScale =
            colorObject.texture_scale *
            (Color.DEFAULT_TEXTURE_PIXEL_RESOLUTION / img.width) *
            (colorObject.textureSizeInMM / Color.DEFAULT_TEXTURE_SIZE_IN_MM) // original formula
        const textureSize = Math.max(1, Math.round(img.width * textureNewScale)) // ensure >= 1 px and integer

        // Resolve destination canvas/context for this slot
        const textureCanvas = document.getElementById('viewimage_texture_' + position) // destination canvas
        if (!textureCanvas) {
            console.warn('Canvas not found for position', position)
            return
        }
        const textureCanvasCtx = textureCanvas.getContext('2d') // destination context

        // Contain all state (transform/composite/path) to this render only
        textureCanvasCtx.save()
        try {
            // --- build the square tile on a temporary canvas ---------------------
            // Size the tile canvas (square) — avoid redundant resizes
            if (tempCanvas.width !== textureSize) tempCanvas.width = textureSize
            if (tempCanvas.height !== textureSize) tempCanvas.height = textureSize

            // Draw the source image into the tile (stretched to square by design)
            const tctx = tempCanvas.getContext('2d')
            tctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height) // remove any previous pixels
            tctx.drawImage(
                img,
                0,
                0,
                img.width,
                img.height, // src rect
                0,
                0,
                textureSize,
                textureSize, // dst rect (square tile)
            )

            // --- prepare destination canvas -------------------------------------
            // Match destination canvas size to the real image dimensions (avoid redundant resets)
            if (textureCanvasCtx.canvas.width !== this._realImageWidth) {
                textureCanvasCtx.canvas.width = this._realImageWidth
            }
            if (textureCanvasCtx.canvas.height !== this._realImageHeight) {
                textureCanvasCtx.canvas.height = this._realImageHeight
            }

            // Clear destination
            textureCanvasCtx.clearRect(0, 0, this._realImageWidth, this._realImageHeight)

            if (colorObject.type === 2) {
                textureCanvasCtx.fillStyle =
                    'rgba(' + colorObject.swatch_color.sr + ',' + colorObject.swatch_color.sg + ',' + colorObject.swatch_color.sb + ', 1)'
                textureCanvasCtx.fillRect(0, 0, this._realImageWidth, this._realImageHeight)
                textureCanvasCtx.globalCompositeOperation = 'source-atop' // keep exact behavior
            }

            // --- pattern creation & draw ----------------------------------------
            const pattern = textureCanvasCtx.createPattern(tempCanvas, 'repeat') // repeating pattern
            if (!pattern) {
                console.warn('createPattern returned null')
                return
            }

            // Compute center (backing pixels)
            const x = textureCanvas.width / 2 // center x
            const y = textureCanvas.height / 2 // center y

            // Define the draw region (fresh path), order preserved
            textureCanvasCtx.beginPath()
            textureCanvasCtx.rect(0, 0, this.viewimage_original.width, this.viewimage_original.height)

            // Translate and rotate (about canvas center)
            textureCanvasCtx.translate(x, y)
            textureCanvasCtx.rotate(angleInRad)

            // Fill the path with the repeating pattern
            textureCanvasCtx.fillStyle = pattern
            textureCanvasCtx.fill()

            // Keep a pixel snapshot so repaint operations can reuse texture values quickly.
            colorObject.texture_data = textureCanvasCtx.getImageData(0, 0, textureCanvas.width, textureCanvas.height)
        } finally {
            // Always restore previous state so nothing leaks into future draws
            textureCanvasCtx.restore()
        }
    }

    /**
     * Rotates a single canvas 90° clockwise: resizes to (ch, cw), redraws the
     * previous bitmap into the rotated context, then resolves.
     *
     * @param {Object} canvas  Target with {element, context, width, height, size()}.
     * @param {number} cw      Original width before rotation.
     * @param {number} ch      Original height before rotation.
     * @returns {Promise<void>}
     */
    async rotateCanvas(canvas, cw, ch) {
        const element = canvas.element
        const ctx = canvas.context

        // Get a drawable source without base64
        let src
        try {
            if (typeof createImageBitmap === 'function') {
                src = await createImageBitmap(element)
            } else {
                const tmp = document.createElement('canvas')
                tmp.width = element.width
                tmp.height = element.height
                tmp.getContext('2d').drawImage(element, 0, 0)
                src = tmp // drawImage accepts canvases
            }
        } catch {
            // last-resort fallback mirrors your old approach
            const img = new Image()
            img.src = element.toDataURL()
            await new Promise((r, rej) => {
                img.onload = r
                img.onerror = rej
            })
            src = img
        }

        // Resize target first; keep method bound to canvas
        canvas.size(ch, cw)

        // Clear and draw with a single rotate+translate matrix
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.clearRect(0, 0, ch, cw)

        // 90° CW => (x,y)->(y,-x), then translate by new width
        ctx.setTransform(0, 1, -1, 0, ch, 0)
        ctx.drawImage(src, 0, 0)

        // Restore identity
        ctx.setTransform(1, 0, 0, 1, 0, 0)
    }

    /**
     * Rotates all view canvases 90°, swaps width/height, clamps to max size,
     * reloads image data, and reinitializes image segmentation.
     *
     * @param {Object} toolManager  Manager used to re-init processing.
     * @returns {void}
     */
    async rotate(toolManager) {
        const canvases = [this.viewimage_draw, this.viewimage, this.viewimage_gray, this.viewimage_original]

        // Assume all have same size; optionally assert in dev
        const cw = canvases[0].width
        const ch = canvases[0].height

        await Promise.all(canvases.map((c) => this.rotateCanvas(c, cw, ch)))

        // After rotation, logical W/H are swapped
        let newW = ch
        let newH = cw

        // Clamp using a uniform scale factor to keep aspect ratio
        const maxSide = Math.max(newW, newH)
        if (maxSide > CanvasState.maxImageSize) {
            const k = CanvasState.maxImageSize / maxSide
            newW = Math.round(newW * k)
            newH = Math.round(newH * k)
        }

        this._realImageWidth = newW
        this._realImageHeight = newH

        this.setNewCanvasDimension()

        // Reload pixel buffers (order doesn’t really matter if independent)
        this.viewimage_gray.loadData()
        this.viewimage_original.loadData()
        this.viewimage.loadData()

        // Re-init segmentation after geometry change
        toolManager.initImageSegmentationProcessor()
    }

    /**
     * Refresh canvas sizing when the window layout changes.
     * @returns {void}
     */
    reportWindowSize() {
        this.setNewCanvasDimension()
        this.fitToViewport()
    }

    /**
     * Draw the main image and prep optional mask texture as ImageData.
     *
     * - Clears canvases and draws the provided image on preview & original.
     * - Applies grayscale preview and initializes the internal mask buffer.
     * - Persists canvas-side buffers (viewimage_original/viewimage).
     * - If mask base64 is present, decodes it to ImageData for later registration.
     *
     * @param {{ imageB64: string, maskB64: string|null, maskFormat: string, masks: Array<any> }} dto
     * @returns {Promise<{ masks: Array<any>, maskTextureImageData: ImageData|null }>}
     */
    async renderPhotoToCanvas({ imageB64, maskB64, maskFormat, masks }) {
        console.log('--- renderPhotoToCanvas --- ')

        // Load main image (data URL already built by the DTO)
        const img = await Utils.loadImage(imageB64)
        this._drawImageToCanvases(img)

        // If a mask texture exists, decode it to ImageData for later registration
        const maskTextureImageData = await this._loadMaskTextureImageDataFromUrl(
            maskB64 ? `data:image/${maskFormat || 'png'};base64,${maskB64}` : null,
        )

        return {
            masks,
            maskTextureImageData,
        }
    }

    /**
     * Renders a prepaint view from an original photo: draws the source image,
     * prepares grayscale/mask buffers, imports color sample colors, optionally applies a
     * mask texture, then refreshes the result.
     *
     * @param {Object} [opts={}]
     * @param {string} opts.originalURL                  Source image URL (required).
     * @param {string|null} [opts.maskURL=null]          Optional mask texture URL.
     * @param {Object[]} [opts.masks=[]]                 Color sample entries passed to applyMaskColorSampleAndTexture.
     * @param {number|string} [opts.current_swatch_id]
     * @param {Array<number|string>} [opts.listOfColorChartsServerID]
     * @returns {Promise<void>}
     */
    async renderPrepaintFromPrepaintPhoto({
        originalURL,
        maskURL = null,
        masks = [],
        current_swatch_id,
        listOfColorChartsServerID,
    } = {}) {
        // 1) Reset & draw original
        const img = await Utils.loadImage(originalURL)
        this._drawImageToCanvases(img)

        // 3) Import color sample entries and optional mask texture, then repaint
        const maskTextureImageData = await this._loadMaskTextureImageDataFromUrl(maskURL)

        await this.applyMaskColorSampleAndTexture({
            colorArray: masks,
            maskTextureImageData: maskTextureImageData,
            current_swatch_id: current_swatch_id,
            listOfColorChartsServerID: listOfColorChartsServerID,
        })
    }

    /**
     * Apply a color sample + optional mask texture and refresh the result.
     * Entry point for DB load, upload import, and prepaint flows.
     * - Imports color sample entries into dropcolors.
     * - Registers the mask texture (if provided).
     * - Refreshes computed results.
     * @param {Object} [opts={}]
     * @param {Object[]} opts.colorArray
     * @param {ImageData|null} [opts.maskTextureImageData=null]
     * @param {number|string} [opts.current_swatch_id]
     * @param {Array<number|string>} [opts.listOfColorChartsServerID]
     * @returns {Promise<Map<number, number>>}
     */
    async applyMaskColorSampleAndTexture({
        colorArray,
        maskTextureImageData = null,
        current_swatch_id,
        listOfColorChartsServerID,
    } = {}) {
        const currentSwatchId = current_swatch_id
        const importer = new MaskColorSampleImporter({
            canvasState: this,
            currentSwatchId: currentSwatchId,
            listOfColorChartsServerID,
        })

        const shiftCounter = await importer.importColorSample(colorArray)

        if (maskTextureImageData) {
            await this.registerAndDisplayMasks(maskTextureImageData, shiftCounter)
        }

        await this.rebuildPaintedImage()

        return shiftCounter
    }

    /**
     * Load a mask texture URL (or data URL) into ImageData sized to the canvas.
     * @param {string|null} maskURL
     * @returns {Promise<ImageData|null>}
     */
    async _loadMaskTextureImageDataFromUrl(maskURL) {
        if (!maskURL) return null

        try {
            const maskImg = await Utils.loadImage(maskURL)
            return await Utils.imageToImageData(maskImg, this._realImageWidth, this._realImageHeight, true)
        } catch (e) {
            console.warn('[CanvasState] mask texture not available or failed to load:', e)
            return null
        }
    }

    /**
     * Draw an image into the preview/original canvases and prep mask buffers.
     * @param {HTMLImageElement} img
     * @returns {void}
     */
    _drawImageToCanvases(img) {
        // Reset drawing surface
        this.clearCanvas()

        // Compute and store target size
        this.setImageSize(img.width, img.height)

        // Draw on preview + original canvases
        this.viewimage.context.drawImage(img, 0, 0, this._realImageWidth, this._realImageHeight)
        this.viewimage_original.context.drawImage(img, 0, 0, this._realImageWidth, this._realImageHeight)

        // Apply grayscale preview and init mask buffer
        this.drawImageInGrayscale(this.getResultImageImageData())
        this._maskImage.initWithSize(this._realImageWidth, this._realImageHeight)

        // Persist canvas-side buffers for later ops
        this.viewimage_original.loadData()
        this.viewimage.loadData()
    }
}

export default CanvasState
